import { exportKeychain, exportPem } from '@plug-core/handlers/walletActions/wallet'
import { useCurrentWallet } from '@plug-core/state'
import { Button, Dialog, Heading, Input, ListGroup, ListRow, Text, cn } from '@ui'
import { ROUTES } from '@utils/routes'
import saveFile from '@utils/saveFile'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { HiExclamation, HiEyeOff, HiLockOpen, HiSupport } from 'react-icons/hi'
import { useLocation, useNavigate } from 'react-router-dom'

interface AlertItem {
  icon: IconType
  text: string
  /** Text colour utility for the icon. */
  color: string
}

const KeyRevealAcceptance = () => {
  const { t } = useTranslation()
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [password, setPassword] = useState<string>('')
  const { currentWallet } = useCurrentWallet()
  const [passwordError, setPasswordError] = useState(false)
  const navigate = useNavigate()

  const { state } = useLocation()

  const items: AlertItem[] = useMemo(() => {
    if (state?.isPem) {
      return [
        {
          text: t('keyRevealAcceptance.pemDownloadAlert'),
          icon: HiExclamation,
          color: 'text-danger',
        },
        {
          text: t('keyRevealAcceptance.accessPem'),
          icon: HiLockOpen,
          color: 'text-warning',
        },
        {
          text: t('keyRevealAcceptance.unencryptedPem'),
          icon: HiEyeOff,
          color: 'text-success',
        },
        {
          text: t('keyRevealAcceptance.pemSupport'),
          icon: HiSupport,
          color: 'text-info',
        },
      ]
    }
    return [
      {
        text: t('keyRevealAcceptance.doNotShare'),
        icon: HiExclamation,
        color: 'text-danger',
      },
      {
        text: t('keyRevealAcceptance.hide'),
        icon: HiEyeOff,
        color: 'text-success',
      },
      {
        text: t('keyRevealAcceptance.access'),
        icon: HiLockOpen,
        color: 'text-warning',
      },
      {
        text: t('keyRevealAcceptance.support'),
        icon: HiSupport,
        color: 'text-info',
      },
    ]
  }, [t, state])

  const callKeychainExport = useCallback(() => {
    if (state?.isPem) {
      exportPem({ password: password || '', principal: currentWallet })
        .then((pemFile) => {
          saveFile(`${currentWallet}.pem`, pemFile)
          navigate(-1)
        })
        .catch((e) => {
          setPasswordError(true)
        })

      return
    }
    exportKeychain({ password: password || '', principal: currentWallet })
      .then((mnemonic) => {
        navigate(ROUTES.BACKUP_SEED, {
          state: { mnemonic, fromSettings: true },
          replace: true,
        })
      })
      .catch((e) => {
        setPasswordError(true)
      })
  }, [password, currentWallet, state])

  return (
    <div className="mb-3 flex flex-col items-center gap-4">
      <span className="grid size-[70px] place-items-center rounded-full bg-surface text-warning">
        <HiExclamation size={30} aria-hidden="true" />
      </span>
      <Heading level={4} align="center">
        {t('keyRevealAcceptance.beforeWeProceed')}
      </Heading>
      <ListGroup className="w-full">
        {items.map((item, index) => (
          <ListRow
            key={index}
            leading={
              <item.icon size={25} aria-hidden="true" className={cn('shrink-0', item.color)} />
            }
          >
            <Text variant="body-s" className="min-w-0 flex-1">
              {item.text}
            </Text>
          </ListRow>
        ))}
      </ListGroup>
      <Button variant="primary" onClick={() => setShowPasswordPrompt(true)} fullWidth>
        {t(`keyRevealAcceptance.${state?.isPem ? 'downloadPem' : 'revealSeedPhrase'}`)}
      </Button>

      <Dialog
        open={showPasswordPrompt}
        onClose={() => setShowPasswordPrompt(false)}
        title={t('keyRevealAcceptance.passwordTitle')}
        description={t('keyRevealAcceptance.passwordDescription')}
        actions={
          <Button variant="primary" disabled={!password} onClick={callKeychainExport} fullWidth>
            {t('common.continue')}
          </Button>
        }
      >
        <Input
          placeholder={t('common.password')}
          aria-label={t('common.password')}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          error={passwordError}
          onKeyDown={(e) => e.key === 'Enter' && callKeychainExport()}
          className="text-left"
          autoFocus
        />
      </Dialog>
    </div>
  )
}

export default KeyRevealAcceptance
